import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { ContractDate, CreateContractDate, UpdateContractDate } from '../types';

/**
 * Contract milestone (ContractDate) service
 *
 * Hooks for the nested /contracts/{contractId}/dates endpoints. Every mutation
 * invalidates the contract's milestone list and the dashboard queries, because
 * the Upcoming Milestones card reads the same rows.
 */

const fetchContractDates = async (contractId: string): Promise<ContractDate[]> => {
  return api.get<ContractDate[]>(`/contracts/${contractId}/dates`);
};

const createContractDate = async ({ contractId, data }: { contractId: string; data: CreateContractDate }): Promise<ContractDate> => {
  return api.post<ContractDate>(`/contracts/${contractId}/dates`, data);
};

const updateContractDate = async ({ contractId, id, data }: { contractId: string; id: string; data: UpdateContractDate }): Promise<ContractDate> => {
  return api.put<ContractDate>(`/contracts/${contractId}/dates/${id}`, data);
};

const deleteContractDate = async ({ contractId, id }: { contractId: string; id: string }): Promise<void> => {
  return api.delete<void>(`/contracts/${contractId}/dates/${id}`);
};

export const useContractDates = (contractId: string) => {
  return useQuery({
    queryKey: queryKeys.contractDates.byContract(contractId),
    queryFn: () => fetchContractDates(contractId),
    enabled: !!contractId,
  });
};

export const useCreateContractDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createContractDate,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractDates.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

export const useUpdateContractDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateContractDate,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractDates.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

export const useDeleteContractDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteContractDate,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contractDates.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};
